import {Box, Button, Typography} from '@viptech/react-components'
import {useMemo, useState} from 'react'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import {BtnClose} from 'src/components/BtnClose'
import {ConfirmationModal} from 'src/components/ConfirmationModal/ConfirmationModal'
import ModalCustom from 'src/components/modal/ModalCustom'
import useAlarmCenterZones from 'src/hooks/api/alarm-center/useAlarmCenterZones'
import useDeleteAlarmCenterZone from 'src/hooks/api/alarm-center/useDeleteAlarmCenterZone'
import useCameras from 'src/hooks/api/camera/useCameras'
import {AlarmCenter, AlarmCenterZone} from 'src/services/api/endpoints/AlarmCenterEndpoint'
import {formatAlarmCenterLabel, formatLineModelLabel, padPartition, padZoneNumber} from '../alarmCentersHelpers'
import AlarmCenterZoneFormModal from './AlarmCenterZoneFormModal'
import {AlarmCenterZoneRowOption} from './AlarmCenterZoneRowOption'

type Props = {
  isOpen: boolean
  alarmCenter: AlarmCenter | null
  onClose: () => void
}

function AlarmCenterZonesModal({isOpen, alarmCenter, onClose}: Props) {
  const canCreate = ValidateUserPermissions('alarm-centers:create')

  const [formOpen, setFormOpen] = useState(false)
  const [editingZone, setEditingZone] = useState<AlarmCenterZone | null>(null)
  const [deletingZone, setDeletingZone] = useState<AlarmCenterZone | null>(null)

  const alarmCenterId = alarmCenter?.id ?? 0

  const {data, isLoading, isError, refetch} = useAlarmCenterZones({
    alarmCenterId,
    enabled: isOpen && alarmCenterId > 0,
  })

  const zones = useMemo(() => data?.entities ?? [], [data])

  const {data: camerasResponse} = useCameras({
    clientIds: alarmCenter ? [alarmCenter.clientId] : undefined,
    enabled: isOpen && alarmCenter != null,
  })

  const deviceNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const device of camerasResponse?.data.data.entities ?? []) {
      map.set(device.id, device.name)
    }
    return map
  }, [camerasResponse])

  const deleteZone = useDeleteAlarmCenterZone()

  const handleClose = () => {
    setFormOpen(false)
    setEditingZone(null)
    setDeletingZone(null)
    onClose()
  }

  const handleConfirmDelete = async () => {
    if (!deletingZone) return
    try {
      await deleteZone.mutateAsync(deletingZone.id)
      setDeletingZone(null)
      void refetch()
    } catch {}
  }

  if (!alarmCenter) return null

  return (
    <>
      <ModalCustom isOpen={isOpen} closeModal={handleClose} backgroundColor='white' width='640px' minHeight='520px'>
        <div style={{padding: '32px 40px', display: 'flex', flexDirection: 'column', gap: 20, minHeight: '480px'}}>
          <BtnClose onClose={handleClose} />

          <Box>
            <Typography variant='p' size='20px' style={{fontWeight: 500}} margin='0'>
              Zonas da central
            </Typography>
            <Typography variant='span' size='14px' color='#8E8E8E'>
              Gerencie as zonas vinculadas à central selecionada.
            </Typography>
          </Box>

          <Box display='flex' direction='column' rowGap='8px' style={{background: '#F9FAFB', border: '1px solid #EAECF0', borderRadius: 12, padding: 16}}>
            <Typography variant='span' size='12px' color='#667085'>
              CENTRAL
            </Typography>
            <Typography variant='span' size='14px' color='#101828'>
              {formatAlarmCenterLabel(alarmCenter)}
            </Typography>
            <Typography variant='span' size='12px' color='#667085' style={{marginTop: 8}}>
              MODELO
            </Typography>
            <Typography variant='span' size='14px' color='#101828'>
              {formatLineModelLabel(alarmCenter.lineModel)}
            </Typography>
          </Box>

          <Box display='flex' justifyContent='space-between' align='center'>
            <Typography variant='span' size='14px' color='#344054'>
              {isError ? 'Erro ao carregar' : `${zones.length} ${zones.length === 1 ? 'zona' : 'zonas'}`}
            </Typography>
            {canCreate ? (
              <Button
                fontSize='12px'
                height='36px'
                variant='contained'
                color='#009eff'
                onClick={() => {
                  setEditingZone(null)
                  setFormOpen(true)
                }}>
                Adicionar zona
              </Button>
            ) : null}
          </Box>

          <Box style={{flex: 1, minHeight: 220, overflowY: 'auto'}}>
            {isLoading ? (
              <Typography variant='span' size='14px' color='#98A2B3'>
                Carregando zonas...
              </Typography>
            ) : null}

            {!isLoading && isError ? (
              <Box style={{border: '1px dashed #D0D5DD', borderRadius: 12, padding: 24, textAlign: 'center'}}>
                <Typography variant='span' size='14px' color='#667085'>
                  Não foi possível carregar as zonas. Tente novamente.
                </Typography>
              </Box>
            ) : null}

            {!isLoading && !isError && zones.length === 0 ? (
              <Box style={{border: '1px dashed #D0D5DD', borderRadius: 12, padding: 32, textAlign: 'center'}}>
                <Typography variant='span' size='14px' color='#344054'>
                  Nenhuma zona cadastrada
                </Typography>
                <Typography variant='span' size='13px' color='#667085' style={{display: 'block', marginTop: 8}}>
                  Adicione zonas para vincular dispositivos a esta central de alarme.
                </Typography>
              </Box>
            ) : null}

            {!isLoading && !isError && zones.length > 0 ? (
              <Box display='flex' direction='column' rowGap='8px'>
                {zones.map((zone) => {
                  const title = zone.name?.trim() || `Zona ${padZoneNumber(zone.zoneNumber)}`
                  const deviceName = deviceNameById.get(zone.cameraId) ?? `Dispositivo #${zone.cameraId}`

                  return (
                    <Box
                      key={zone.id}
                      display='flex'
                      justifyContent='space-between'
                      align='center'
                      style={{border: '1px solid #EAECF0', borderRadius: 12, padding: '12px 16px'}}>
                      <Box display='flex' direction='column' rowGap='4px'>
                        <Typography variant='span' size='14px' color='#101828'>
                          {title}
                        </Typography>
                        <Typography variant='span' size='12px' color='#667085'>
                          Zona {padZoneNumber(zone.zoneNumber)} · Part. {padPartition(zone.partition) || '--'} · {deviceName}
                        </Typography>
                      </Box>
                      <AlarmCenterZoneRowOption
                        onEdit={() => {
                          setEditingZone(zone)
                          setFormOpen(true)
                        }}
                        onDelete={() => setDeletingZone(zone)}
                      />
                    </Box>
                  )
                })}
              </Box>
            ) : null}
          </Box>
        </div>
      </ModalCustom>

      <AlarmCenterZoneFormModal
        isOpen={formOpen}
        alarmCenter={alarmCenter}
        zone={editingZone}
        onClose={() => {
          setFormOpen(false)
          setEditingZone(null)
          void refetch()
        }}
      />

      <ConfirmationModal
        title='Excluir zona'
        content={`Deseja excluir a zona ${deletingZone ? padZoneNumber(deletingZone.zoneNumber) : ''}? Esta ação não poderá ser desfeita.`}
        isOpen={deletingZone != null}
        zIndex={20}
        onClose={() => setDeletingZone(null)}
        buttonsContent={[
          {
            label: 'Cancelar',
            onClick: () => setDeletingZone(null),
            variant: 'outlined',
            color: '#8E8E8E',
          },
          {
            label: 'Confirmar',
            onClick: () => void handleConfirmDelete(),
            variant: 'contained',
            dangerAction: true,
          },
        ]}
      />
    </>
  )
}

export default AlarmCenterZonesModal
